import { Box, Button, CircularProgress } from '@ouster/webviz-react-components';

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { RiUserSearchLine } from 'react-icons/ri';
import UserDelete from 'src/components/adminDashboard/UserDelete';
import useChakraHook from 'src/hooks/useChakraHook';

import { createAlert } from './DevToolRecordingsMgmt';
import DashboardCard from '../../components/adminDashboard/DashboardCard';
import UserGdprCheckResult from '../../components/adminDashboard/UserGdprCheckResult';
import UserLookUpEula from '../../components/adminDashboard/UserLookUpEula';
import UserLookUpKeycloakInfo from '../../components/adminDashboard/UserLookUpKeycloakInfo';
import UserLookUpOrganization from '../../components/adminDashboard/UserLookUpOrganization';
import UserLookUpSearch from '../../components/adminDashboard/UserLookUpSearch';
import UserLookUpStudioInfo from '../../components/adminDashboard/UserLookUpStudioInfo';
import UserUploaderStatusSync from '../../components/adminDashboard/UserUploaderStatusSync';
import { useStoreActions, useStoreState } from '../../reducers';

const EMAIL_VERIFICATION_UPDATE_BATCH_SIZE = 50;
const BATCH_PROCESSING_DELAY_MS = 200; // Delay between batch requests

export default function UserLookUp() {
  const {
    userLookupResult,
    isLoadingUserLookup,
    isLoadingGdprDataCheck,
    verifiedEmailSyncProgress,
    compareUsersOrgsResult,
    gdprDataCheckResult: userGdprInfo,
  } = useStoreState((storeState) => storeState.adminDashboard);

  const {
    checkUserDataForGdpr,
    getUserInfoByEmail,
    clearUserLookup,
    clearGdprUserLookup,
    syncStudioEmailVerificationToKeycloak,
    setVerifiedEmailSyncProgress,
    resetSyncVerifiedEmailProgress,
    compareUsersOrgsWithUploader,
    setCompareUsersOrgsResult,
    setGdprDataCheckResult: setUserGdprInfo,
    deleteUserGdprCompliant,
  } = useStoreActions((actions) => actions.adminDashboard);

  const { curUserOrg } = useStoreState((storeState) => storeState.user);
  const isSupportUser = curUserOrg?.role === 'SUPPORT';

  const [toolMode, setToolMode] = useState<
    'search' | 'syncEmailVerification' | 'checkUploaderStatus' | 'gdprCheck'
  >(() =>
    verifiedEmailSyncProgress.isInProgress ? 'syncEmailVerification' : 'search'
  );
  const [isSyncVerifiedEmail, setIsSyncVerifiedEmail] = useState(false);
  const [isLoadingUploaderStatus, setIsLoadingUploaderStatus] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<ReactElement | null>(
    null
  );
  const isMountedRef = useRef(true);
  const isProcessingBatchRef = useRef(false);
  const nextBatchTimeoutRef = useRef<number | null>(null);
  const emailSyncRunIdRef = useRef(0);

  const { showToast } = useChakraHook();
  const isEmailVerificationActive =
    toolMode === 'syncEmailVerification' ||
    verifiedEmailSyncProgress.isInProgress ||
    isSyncVerifiedEmail ||
    feedbackMessage !== null;
  const isEmailVerificationRunning =
    verifiedEmailSyncProgress.isInProgress || isSyncVerifiedEmail;
  const hasUploaderStatusResult = compareUsersOrgsResult !== null;
  const isUploaderStatusActive =
    toolMode === 'checkUploaderStatus' ||
    isLoadingUploaderStatus ||
    hasUploaderStatusResult;
  const showEmailVerificationPanel = isEmailVerificationActive;
  const showUploaderStatusPanel =
    !showEmailVerificationPanel && isUploaderStatusActive;
  const showGdprPanel =
    !showEmailVerificationPanel &&
    !showUploaderStatusPanel &&
    toolMode === 'gdprCheck' &&
    (isLoadingGdprDataCheck || userGdprInfo !== null);
  const showSearchPanel =
    !showEmailVerificationPanel &&
    !showUploaderStatusPanel &&
    !showGdprPanel &&
    toolMode === 'search' &&
    (isLoadingUserLookup || userLookupResult !== null);
  const showDefaultPanel =
    !showEmailVerificationPanel &&
    !showUploaderStatusPanel &&
    !showGdprPanel &&
    !showSearchPanel &&
    toolMode === 'search';

  const cancelEmailVerification = () => {
    // Invalidate any in-flight async/timer updates from previous runs.
    emailSyncRunIdRef.current += 1;
    if (nextBatchTimeoutRef.current !== null) {
      clearTimeout(nextBatchTimeoutRef.current);
      nextBatchTimeoutRef.current = null;
    }
    resetSyncVerifiedEmailProgress();
    setIsSyncVerifiedEmail(false);
  };

  useEffect(
    () => () => {
      isMountedRef.current = false;
      if (nextBatchTimeoutRef.current !== null) {
        clearTimeout(nextBatchTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const processBatch = async () => {
      const runId = emailSyncRunIdRef.current;
      if (
        !verifiedEmailSyncProgress.isInProgress ||
        isProcessingBatchRef.current
      ) {
        return;
      }
      isProcessingBatchRef.current = true;

      try {
        const result = await syncStudioEmailVerificationToKeycloak({
          page: verifiedEmailSyncProgress.currentPage,
          perPage: EMAIL_VERIFICATION_UPDATE_BATCH_SIZE,
        });

        if (!isMountedRef.current || runId !== emailSyncRunIdRef.current) {
          return;
        }

        const newTotalUpdated =
          verifiedEmailSyncProgress.totalUpdated + result.updated;

        if (result.completed) {
          // Process completed
          const alertElement =
            newTotalUpdated === 0
              ? createAlert('info', 'No users needed updating.', true)
              : createAlert(
                  'success',
                  `${newTotalUpdated} users synced successfully.`,
                  true
                );
          setFeedbackMessage(alertElement);
          resetSyncVerifiedEmailProgress();
          setIsSyncVerifiedEmail(false);
        } else {
          // Continue to next page
          nextBatchTimeoutRef.current = window.setTimeout(() => {
            if (isMountedRef.current && runId === emailSyncRunIdRef.current) {
              setVerifiedEmailSyncProgress({
                totalUpdated: newTotalUpdated,
                currentPage: verifiedEmailSyncProgress.currentPage + 1,
                isInProgress: true,
                progressPercentage: result.progress_percentage || 0,
              });
            }
          }, BATCH_PROCESSING_DELAY_MS); // Small delay between requests
        }
      } catch (error) {
        if (isMountedRef.current && runId === emailSyncRunIdRef.current) {
          setFeedbackMessage(
            createAlert(
              'error',
              `Failed to sync user's email verification status. ${error}`,
              true
            )
          );
          resetSyncVerifiedEmailProgress();
          setIsSyncVerifiedEmail(false);
        }
      } finally {
        isProcessingBatchRef.current = false;
      }
    };

    if (verifiedEmailSyncProgress.isInProgress) processBatch();
  }, [
    verifiedEmailSyncProgress.isInProgress,
    verifiedEmailSyncProgress.currentPage,
    verifiedEmailSyncProgress.totalUpdated,
    syncStudioEmailVerificationToKeycloak,
    setVerifiedEmailSyncProgress,
    resetSyncVerifiedEmailProgress,
  ]);

  const verifyEmail = () => {
    // clearUserLookup();

    emailSyncRunIdRef.current += 1;
    setIsSyncVerifiedEmail(true);
    setIsLoadingUploaderStatus(false);
    setFeedbackMessage(null);
    setCompareUsersOrgsResult(null);

    setToolMode('syncEmailVerification');
    setVerifiedEmailSyncProgress({
      totalUpdated: 0,
      currentPage: 1,
      isInProgress: true,
      progressPercentage: 0,
    });
  };

  const uploaderStatusCheck = async () => {
    cancelEmailVerification();
    setFeedbackMessage(null);
    setToolMode('checkUploaderStatus');
    setCompareUsersOrgsResult(null);
    setIsLoadingUploaderStatus(true);

    try {
      await compareUsersOrgsWithUploader();
    } catch (error) {
      showToast({
        title: 'Failed to check uploader status',
        description: `${error}`,
        status: 'error',
      });
    } finally {
      setIsLoadingUploaderStatus(false);
    }
  };

  const handleSearch = async (email: string) => {
    cancelEmailVerification();
    setIsLoadingUploaderStatus(false);
    setToolMode('search');
    setFeedbackMessage(null);
    setCompareUsersOrgsResult(null);
    try {
      await getUserInfoByEmail(email);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleClearUserLookup = () => {
    clearUserLookup();
  };

  const handleClearGdprInfo = () => {
    clearGdprUserLookup();
    setToolMode('search');
  };

  const handleGdprUserSearch = async (email: string) => {
    cancelEmailVerification();
    setIsLoadingUploaderStatus(false);
    setToolMode('gdprCheck');
    setFeedbackMessage(null);
    setCompareUsersOrgsResult(null);
    setUserGdprInfo(null);
    try {
      await checkUserDataForGdpr(email);
    } catch (error) {
      console.error('GDPR check failed:', error);
    }
  };

  const handleDeleteUser = async (email: string): Promise<boolean> => {
    try {
      await deleteUserGdprCompliant(email);
      showToast({
        title: 'User deleted successfully',
        description: `User with email ${email} has been deleted.`,
        status: 'success',
      });
      clearGdprUserLookup();
      return true;
    } catch (error) {
      showToast({
        title: 'User deletion failed',
        description: `${error}`,
        status: 'error',
      });
      return false;
    }
  };

  return (
    <Box
      display="grid"
      gridTemplateColumns="2fr 5fr"
      gridTemplateRows="1fr"
      height="100%"
      overflow="hidden"
    >
      <DashboardCard
        icon={<RiUserSearchLine size={18} />}
        title="User Management Console"
        data-testid="user-management-console-card"
        style={{
          alignContent: 'start',
          overflow: 'hidden',
          borderRadius: '0px',
        }}
      >
        <Box display="grid" gap={4} overflow="auto">
          <UserLookUpSearch
            handleSearch={handleSearch}
            clearUserLookup={handleClearUserLookup}
          />
          {!isSupportUser && (
            <>
              <UserDelete
                handleSearch={handleGdprUserSearch}
                handleDelete={handleDeleteUser}
                clearGdprInfo={handleClearGdprInfo}
                gdprInfo={userGdprInfo}
              />
              <DashboardCard
                title="Email verification"
                description="Sync email verification status from Studio to Keycloak"
                data-testid="email-verification-console-card"
              >
                <Button
                  variant="outline"
                  onClick={verifyEmail}
                  minWidth="min-content"
                >
                  Verify Email
                </Button>
              </DashboardCard>
              <DashboardCard
                title="Uploader Status Check"
                description="Find the cases where uploader is not synced with Studio"
                data-testid="uploader-status-check-card"
              >
                <Button
                  variant="outline"
                  onClick={uploaderStatusCheck}
                  minWidth="min-content"
                >
                  Check Status
                </Button>
              </DashboardCard>
            </>
          )}
        </Box>
      </DashboardCard>

      {showSearchPanel && (
        <DashboardCard
          title="User Details"
          data-testid="user-detail-card"
          style={{
            height: '100%',
            overflow: 'auto',
            borderRadius: '0px',
            alignContent: 'start',
          }}
        >
          <Box rowGap="0.75rem" display="grid" height="100%" overflow="auto">
            <UserLookUpKeycloakInfo
              isUserFound={userLookupResult !== null}
              keycloakUserInfo={userLookupResult?.auth_server_info ?? null}
              isLoadingUserLookup={isLoadingUserLookup}
            />
            <UserLookUpStudioInfo
              isUserFound={userLookupResult !== null}
              studioUserInfo={userLookupResult?.user ?? null}
              isLoadingUserLookup={isLoadingUserLookup}
            />
            <UserLookUpEula
              isUserFound={userLookupResult !== null}
              eulaInfo={userLookupResult?.eula_information ?? null}
              isLoadingUserLookup={isLoadingUserLookup}
            />
            <UserLookUpOrganization
              organizations={userLookupResult?.organizations ?? []}
              isLoadingUserLookup={isLoadingUserLookup}
              isUserFound={userLookupResult !== null}
            />
          </Box>
        </DashboardCard>
      )}

      {showEmailVerificationPanel && (
        <Box display="grid" placeContent="center" height="100%">
          {isEmailVerificationRunning ? (
            <Box display="grid" gap={8} justifyItems="center">
              <Box as="p" fontSize="xl">
                Syncing email verification status from Studio to Keycloak.
                Please wait...
              </Box>
              <Box as="p" fontSize="lg">
                Progress: {verifiedEmailSyncProgress.totalUpdated} users&apos;
                email verification status have been updated
              </Box>
              <CircularProgress
                isIndeterminate={
                  verifiedEmailSyncProgress.progressPercentage === 0
                }
                value={verifiedEmailSyncProgress.progressPercentage}
                size={20}
              />
            </Box>
          ) : (
            feedbackMessage
          )}
        </Box>
      )}

      {showUploaderStatusPanel &&
        (isLoadingUploaderStatus ? (
          <Box
            display="grid"
            placeContent="center"
            justifyItems="center"
            alignItems="center"
            gap={6}
            textAlign="center"
            height="100%"
          >
            <Box as="p" fontSize="xl">
              Checking uploader sync status. Please wait...
            </Box>
            <CircularProgress isIndeterminate size={20} />
          </Box>
        ) : compareUsersOrgsResult ? (
          <UserUploaderStatusSync
            compareUsersOrgsResult={compareUsersOrgsResult}
          />
        ) : (
          <Box
            border="1px solid"
            borderColor="whiteAlpha.200"
            bgColor="whiteAlpha.50"
            display="grid"
            gap={4}
            placeContent="center"
            justifyItems="center"
          >
            <Box fontSize="lg" color="gray.300">
              No uploader status result available.
            </Box>
            <Button variant="outline" onClick={uploaderStatusCheck}>
              Retry Check Status
            </Button>
          </Box>
        ))}

      {showGdprPanel && (
        <UserGdprCheckResult
          isLoading={isLoadingGdprDataCheck}
          gdprInfo={userGdprInfo}
        />
      )}

      {showDefaultPanel && (
        <Box
          border="1px solid"
          borderColor="whiteAlpha.200"
          bgColor="whiteAlpha.50"
          display="grid"
          gap={6}
          placeContent="center"
          justifyItems="center"
          data-testid="recording-lookup-no-result-box"
        >
          <Box fontSize="2xl" color="gray.300">
            User management console
          </Box>
        </Box>
      )}
    </Box>
  );
}
